import {
  Text,
  Stack,
  Flex,
  Divider,
  Skeleton,
  Alert,
  Button,
} from '@mantine/core';
import { sentimentColors } from 'constants/reporting';
import { ToolFunctionType } from 'interfaces/Reporting';
import Badge from 'components/Badge';
import ReportContext from 'context/ReportContext';
import { FC, useContext } from 'react';
import { sentimentLabels } from '../../utils';
import { Message } from '.';
import { IconVocabularyOff } from '@tabler/icons-react';
import AuthContext from 'context/AuthContext';
import routes from 'utils/routes';
import { useNavigate } from 'react-router-dom';

const toolActions = {
  [ToolFunctionType.TRANSFER_CALL]: 'Transferred call',
  [ToolFunctionType.SEND_MESSAGE]: 'Message taken',
  [ToolFunctionType.END_CONVERSATION]: 'End call',
};

const actionColors = {
  [ToolFunctionType.TRANSFER_CALL]: '#5976AB',
  [ToolFunctionType.SEND_MESSAGE]: '#5D629A',
  [ToolFunctionType.END_CONVERSATION]: 'var(--text-color)',
};

const actionBackgrounds = {
  [ToolFunctionType.TRANSFER_CALL]: '#5976AB12',
  [ToolFunctionType.SEND_MESSAGE]: '#5D629A0D',
  [ToolFunctionType.END_CONVERSATION]: 'var(--bg-color)',
};

interface MessagesProps {
  messages: Message[];
  isUnavailable: boolean;
}

const Messages: FC<MessagesProps> = ({ messages, isUnavailable }) => {
  const {
    data: { isLoadingCallDetails },
  } = useContext(ReportContext);
  const {
    data: { company },
  } = useContext(AuthContext);
  const navigate = useNavigate();

  if (isLoadingCallDetails) {
    return (
      <Stack gap="xl" w="100%">
        {Array(10)
          .fill(null)
          .map((_, index) => (
            <Stack key={index} gap="0.4rem" w="100%">
              <Flex gap={6} style={{ flex: 1 }}>
                <Skeleton h={20} w={35} mr={10} />

                <Flex gap="md" align="center">
                  <Skeleton h={20} w={100} />
                </Flex>
              </Flex>
              <Skeleton h={20} w="100%" />
              <Skeleton h={20} w="100%" />
            </Stack>
          ))}
      </Stack>
    );
  }

  if (isUnavailable || messages.length === 0) {
    return (
      <Stack mt="xl" align="center" justify="center" w="100%" h="100%">
        <IconVocabularyOff
          stroke={0.2}
          size={150}
          color="var(--mantine-color-gray-3)"
        />
        <Text size="sm" fw={600}>
          No transcription available
        </Text>
        {isUnavailable && (
          <>
            <Text size="sm" fw={400}>
              {company?.record_transcript
                ? 'Transcription was turned off at the time of this call.'
                : 'Transcription is currently turned off in your settings'}
            </Text>
            {!company?.record_transcript && (
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  navigate(`${routes.settingsPath.href}/?tab=general`);
                }}
              >
                Go to settings
              </Button>
            )}
          </>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap="xl" w="100%">
      {messages.map((message, index) => {
        const { sentiment, speaker, time, content, isOwner, action } = message;

        return (
          <Alert
            key={`${index}-${time}`}
            variant="default"
            color="blue"
            radius="md"
            styles={{
              root: action
                ? {
                    backgroundColor: actionBackgrounds[action],
                    borderColor: '#5D629A14',
                  }
                : {
                    padding: 0,
                    border: 'none',
                  },
            }}
          >
            <Stack gap="0.4rem">
              <Flex gap={6} style={{ flex: 1 }}>
                <Text size="sm" w={45} c="dimmed">
                  {time}
                </Text>
                <Flex gap="md" align="center">
                  <Text
                    size="sm"
                    fw={600}
                    c={isOwner ? '' : 'var(--primary-color)'}
                  >
                    {speaker}
                  </Text>
                  {(sentiment || action) && (
                    <Divider
                      orientation="vertical"
                      h="1rem"
                      style={{ alignSelf: 'center' }}
                    />
                  )}
                  {sentiment && (
                    <Badge
                      size="md"
                      variant="light"
                      color={sentimentColors[sentiment]}
                      fw={500}
                      style={{ color: '#1A1A1ACC' }}
                    >
                      {sentimentLabels[sentiment]}
                    </Badge>
                  )}
                  {action && (
                    <Badge size="md" color={actionColors[action]} fw={500}>
                      {toolActions[action]}
                    </Badge>
                  )}
                </Flex>
              </Flex>
              {content && (
                <Text size="sm" lh={1.5} opacity={0.8}>
                  {content}
                </Text>
              )}
            </Stack>
          </Alert>
        );
      })}
    </Stack>
  );
};

export default Messages;
